import { Fragment, useRef, useState } from "react";
import { DocumentMetadata } from "@neum/frontend-shared/types/metagraphTypes";
import { Combobox, Transition } from "@headlessui/react";
import {
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
  CheckIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/solid";

interface MultiDocPickerProps {
  pickerLabel: string;
  activeDocumentId: string;
  sessionDocumentsMetadata: DocumentMetadata[];
  tabsDisabled?: boolean;
  onSelectionChange: (documentId: string) => Promise<void>;
  className?: string;
}

export const MultiDocPicker: React.FC<MultiDocPickerProps> = ({
  pickerLabel,
  activeDocumentId,
  sessionDocumentsMetadata,
  tabsDisabled,
  onSelectionChange,
  className = "",
}: MultiDocPickerProps) => {
  const [query, setQuery] = useState("");
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Arrow left - go to previous document
  const goToPreviousDocument = () => {
    if (tabsDisabled || sessionDocumentsMetadata.length <= 1) return;

    const currentIndex = sessionDocumentsMetadata.findIndex(
      (doc) => doc.id === activeDocumentId
    );
    if (currentIndex > 0) {
      // Go to previous document
      void onSelectionChange(sessionDocumentsMetadata[currentIndex - 1].id);
    } else {
      // Wrap around to the last document
      void onSelectionChange(
        sessionDocumentsMetadata[sessionDocumentsMetadata.length - 1].id
      );
    }
  };

  // Arrow right - go to next document
  const goToNextDocument = () => {
    if (tabsDisabled || sessionDocumentsMetadata.length <= 1) return;

    const currentIndex = sessionDocumentsMetadata.findIndex(
      (doc) => doc.id === activeDocumentId
    );
    if (currentIndex < sessionDocumentsMetadata.length - 1) {
      // Go to next document
      void onSelectionChange(sessionDocumentsMetadata[currentIndex + 1].id);
    } else {
      // Wrap around to the first document
      void onSelectionChange(sessionDocumentsMetadata[0].id);
    }
  };

  return (
    <div className={`w-full px-1 ${className}`}>
      <div className="relative w-full">
        <div className="flex flex-row items-center gap-x-2 w-full">
          <div
            onClick={goToPreviousDocument}
            className={`cursor-pointer ${
              tabsDisabled || sessionDocumentsMetadata.length <= 1
                ? "opacity-50"
                : ""
            }`}
          >
            <ArrowLeftCircleIcon className="h-6 w-6 text-blue-600 hover:text-blue-500 transition-colors" />
          </div>
          <div className="relative w-full">
            <label
              htmlFor="docs-tabs-combobox" // Changed ID for clarity
              className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-main-text-darker z-10" // Ensure label is above combobox input
            >
              {pickerLabel}
            </label>
            <Combobox
              value={activeDocumentId}
              onChange={(documentId) => {
                buttonRef.current?.click();
                void onSelectionChange(documentId);
              }}
              disabled={tabsDisabled}
            >
              {({ open }) => (
                <div className="relative w-full">
                  <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-blue-300 sm:text-sm border border-gray-300 flex flex-row items-center">
                    <Combobox.Input
                      id="docs-tabs-combobox" // Added ID
                      className={`w-full border-none focus:border-none py-2 pl-3 mr-10 text-sm leading-5 ${tabsDisabled ? "text-gray-500" : "text-gray-900"} focus:outline-none focus:ring-0 h-full rounded-md`}
                      displayValue={(documentId: string) => {
                        const doc = sessionDocumentsMetadata.find(
                          (d) => d.id === documentId
                        );
                        return doc?.title ?? "";
                      }}
                      onChange={(event) => setQuery(event.target.value)}
                      onClick={() => {
                        if (!open) {
                          buttonRef.current?.click();
                        }
                      }}
                    />
                    <Combobox.Button
                      className="absolute inset-y-0 right-0 flex items-center pr-2"
                      ref={buttonRef}
                    >
                      <ChevronUpDownIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </Combobox.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    afterLeave={() => setQuery("")}
                  >
                    <Combobox.Options className="absolute mt-1 max-h-40 thin-scrollbar min-w-60 w-[90vw] md:w-full left-1/2 -translate-x-1/2 md:left-0 md:translate-x-0 overflow-auto rounded-md bg-white py-1 px-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm cursor-pointer z-20">
                      {sessionDocumentsMetadata.length === 0 ? (
                        <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                          No documents found.
                        </div>
                      ) : (
                        sessionDocumentsMetadata
                          .filter(
                            (doc) =>
                              query === "" ||
                              doc.title
                                ?.toLowerCase()
                                .includes(query.toLowerCase())
                          )
                          .map((doc) => (
                            <Combobox.Option
                              key={doc.id}
                              className={({ active }) =>
                                `relative select-none py-2 pl-4 flex flex-row gap-x-2 pr-4 cursor-pointer ${
                                  active
                                    ? "bg-main-lighter text-white" // Adjusted active style
                                    : "text-main-text-darker"
                                }`
                              }
                              value={doc.id}
                            >
                              {({ selected }) => (
                                <>
                                  <span
                                    className={`block truncate ${
                                      selected ? "font-medium" : "font-normal"
                                    }`}
                                  >
                                    {doc.title}
                                  </span>
                                  {selected && (
                                    <span className="flex items-center pl-3 text-gray-600">
                                      <CheckIcon
                                        className="h-5 w-5"
                                        aria-hidden="true"
                                      />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                          ))
                      )}
                    </Combobox.Options>
                  </Transition>
                </div>
              )}
            </Combobox>
          </div>
          <div
            onClick={goToNextDocument}
            className={`cursor-pointer ${
              tabsDisabled || sessionDocumentsMetadata.length <= 1
                ? "opacity-50"
                : ""
            }`}
          >
            <ArrowRightCircleIcon className="h-6 w-6 text-blue-600 hover:text-blue-500 transition-colors" />
          </div>
        </div>
      </div>
    </div>
  );
};
